import { useCallback, useState } from 'react';
import { SDKResponse } from '@commercetools/frontend-sdk';
import { useSelector } from 'react-redux';
import { useLocalStorage, useMedia } from 'react-use';
import app from '@frontastic/catwalk/src/js/app/app';
import { ApgStore } from '../../../../types/store-locator/ApgStore';
import { sdk } from '../../../sdk';

const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

type Coordinates = { latitude: string; longitude: string };
type ErrorCallback = (error: GeolocationPositionError) => void;
type StoreItem = Record<string, any>;
type Bounds = { south: number; north: number; west: number; east: number };

interface CoordinatesInfo {
  lat: number;
  lng: number;
}

export const getDistance = (point1: CoordinatesInfo, point2: CoordinatesInfo): number => {
  // a = sin²(Δφ/2) + cos φ1 ⋅ cos φ2 ⋅ sin²(Δλ/2)
  // c = 2 ⋅ atan2( √a, √(1−a) )
  // d = R ⋅ c

  // φ is latitude in radians, λ is longitude in radians;
  // R is earth’s radius (mean radius = 6,371km);

  const R = 6371e3; // Radius of the earth in metres
  const φ1 = (point1.lat * Math.PI) / 180;
  const φ2 = (point2.lat * Math.PI) / 180;
  const Δφ = ((point2.lat - point1.lat) * Math.PI) / 180;
  const Δλ = ((point2.lng - point1.lng) * Math.PI) / 180;

  const a = Math.pow(Math.sin(Δφ / 2), 2) + Math.cos(φ1) * Math.cos(φ2) * Math.pow(Math.sin(Δλ / 2), 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
};

export const getGeoLocation = async (errorCallback: ErrorCallback): Promise<CoordinatesInfo | string | undefined> => {
  const value = typeof localStorage !== 'undefined' ? localStorage.getItem('userLocation') : undefined;
  if (value) return value;

  if (!navigator.geolocation) {
    throw new Error('Geolocation is not supported by your browser');
  } else {
    return new Promise<CoordinatesInfo>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const result: CoordinatesInfo = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          localStorage.setItem('userLocation', JSON.stringify(result));
          resolve(result);
        },
        (err) => {
          errorCallback(err);
          reject(new Error('Unable to retrieve your location'));
        },
      );
    });
  }
};

const getSchedules = (item: StoreItem): Array<{ day: string; time: string }> => {
  const schedules: Array<{ day: string; time: string }> = [];
  days.forEach((el) => {
    if (!item[`hours_${el.slice(0, 3)}_open`] && !item[`hours_${el.slice(0, 3)}_close`]) return;

    schedules.push({
      day: el.slice(0, 1).toUpperCase() + el.slice(1),
      time: `${item[`hours_${el.slice(0, 3)}_open`]} - ${item[`hours_${el.slice(0, 3)}_close`]}`,
    });
  });

  return schedules;
};

export const getSpecialHoursArray = (item: StoreItem): Array<{ name: string; value: string }> => {
  const arr = Array.from({ length: 10 }, (_, index) => index + 1);

  return arr
    .map((x) => ({
      name: item[`hours_special_description_${x}`],
      value: item[`hours_special_${x}`],
    }))
    .filter((v) => !!v.name && !!v.value);
};

export const getSpecialPrices = (item: StoreItem): string[] => {
  const keys = Object.keys(item).filter((k) => k.startsWith('hours_special_'));
  const sortedKeys = keys.sort((a, b) =>
    a.localeCompare(b, undefined, {
      numeric: true,
      sensitivity: 'base',
    }),
  );
  const values = Object.entries(item || {}).filter(([k]) => sortedKeys.includes(k));

  return values.map((opt) => opt[1]).filter(Boolean);
};

interface StoreData {
  address1: string;
  address2?: string;
  city?: string;
  state: string;
  postcode: string;
  country?: string;
  coordinates: { lat: string; lng: string };
  collect?: boolean;
}

interface StoreResponse {
  data: StoreData[];
}

export const getStores = async ({
  setStoreList,
  skus = [],
  coordinates,
  onlyCollected = false,
  cartId = null,
  setError,
  setLoading = () => null,
}: {
  setStoreList: (stores: any[]) => void;
  skus?: string[] | string;
  coordinates?: Coordinates;
  onlyCollected?: boolean;
  cartId?: string | null;
  setError?: (value: boolean) => void;
  setLoading?: (value: boolean) => void;
}): Promise<void> => {
  setLoading(true);
  const store = app.getStore();
  const locale = store.getState().app.context.locale;
  const currentCountry = locale === 'en_AU@AUD' ? 'Australia' : 'New Zealand';

  const actionName = skus?.length ? '/apg-storelocator/getProductStoresList' : '/apg-storelocator/getStoresList';

  try {
    const response: SDKResponse<Array<ApgStore>> = await sdk.callAction({
      actionName,
      payload: {
        skus,
        cartId,
        longitude: coordinates?.longitude,
        latitude: coordinates?.latitude,
      },
    });

    let data = [];

    if ('data' in response) {
      data = response.data;
    }

    setLoading(false);
    setError && setError(false);
    if (!data.length) {
      setError && setError(true);
      setStoreList([]);
      return;
    }

    const availableStores = data.filter((x: any) => {
      const storesCountry = x.country?.trim();
      return !x.address1?.includes('Closed') && storesCountry.toUpperCase() === currentCountry.toUpperCase();
    });

    const listStores = availableStores.map((item: any, index: number) => {
      const todayDay = days[new Date().getDay()];
      const todaySchedule = getSchedules(item).find((x) => x.day.toUpperCase() === todayDay.toUpperCase());
      return {
        address: `${item.address1} ${item.address2 ? item.address2 + ',' : ''} ${item.city ? item.city + ',' : ''} ${
          item.state
        } ${item.postcode}`,
        storeId: `${index}`,
        ...item,
        coordinates: {
          lat: Number(item.coordinates.lat),
          lng: Number(item.coordinates.lng),
        },
        todayHour: todaySchedule,
        schedules: getSchedules(item),
        specialPrices: getSpecialPrices(item),
      };
    });

    if (onlyCollected) {
      const onlyCollectedStores = listStores.filter((store: any) => store.collect);
      setError && setError(!onlyCollectedStores.length);
      setStoreList(onlyCollectedStores);
      return;
    }

    setStoreList(listStores);
  } catch {
    setLoading(false);
    setError && setError(true);
    setStoreList([]);
  }
};

export const getRadius = (coords1: CoordinatesInfo, coords2: CoordinatesInfo): number => {
  const R = 6371; // Radius of the earth in km
  const dLat = toRad(Number(coords2.lat) - Number(coords1.lat));
  const dLon = toRad(Number(coords2.lng) - Number(coords1.lng));
  const lat1 = toRad(coords1.lat);
  const lat2 = toRad(coords2.lat);

  const a = Math.sin(dLat / 2) ** 2 + Math.sin(dLon / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

export const insideBounds = ({ lat, lng }: CoordinatesInfo, { south, north, west, east }: Bounds): boolean =>
  south <= lat && lat <= north && west <= lng && lng <= east;
// Converts numeric degrees to radians
const toRad = (Value) => {
  return (Value * Math.PI) / 180;
};

const ensureGoogleMapsApiIsLoaded = (googleMapUrl: string): Promise<void> => {
  return new Promise((resolve) => {
    if ((window as any).google?.maps) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.onload = () => resolve();
    script.src = googleMapUrl;
    document.head.appendChild(script);
  });
};

const geocodeByPostcode = async (postcode: string, country: string) => {
  const store = app.getStore();
  const googleKey = store.getState().app.context?.project?.configuration?.googleMap?.key;

  const googleMapUrl = `https://maps.googleapis.com/maps/api/js?key=${googleKey}&v=3.exp&libraries=geometry,drawing,places`;
  await ensureGoogleMapsApiIsLoaded(googleMapUrl);

  const geocoder = new (window as any).google.maps.Geocoder();

  const { results } = await geocoder.geocode({
    address: `${postcode}`,
    componentRestrictions: { country },
  });

  if (!results?.[0]) return undefined;

  return {
    latitude: results[0].geometry.location.lat(),
    longitude: results[0].geometry.location.lng(),
  };
};

const getPostcodeByLatLong = async (lat: number, long: number): Promise<string | undefined> => {
  const store = app.getStore();
  const googleKey = store.getState().app.context?.project?.configuration?.googleMap?.key;

  const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${long}&sensor=true&key=${googleKey}`;
  const response = await fetch(url);
  const data = await response.json();

  const postcode = data?.results?.[0]?.address_components?.find((item: any) => item.types?.includes('postal_code'));

  return postcode?.long_name;
};

const geocodeByPostcodePromises: Record<string, Promise<{ latitude: string; longitude: string } | undefined>> = {};
const postcodeByLatLongPromises: Record<string, Promise<string | undefined>> = {};

export const getStoreCoordinates = async (
  data: { postCode: string },
  country = 'AU',
): Promise<{ latitude: string; longitude: string } | undefined> => {
  const { postCode } = data;
  if (geocodeByPostcodePromises[postCode]) {
    return geocodeByPostcodePromises[postCode];
  }

  geocodeByPostcodePromises[postCode] = geocodeByPostcode(postCode, country);
  return geocodeByPostcodePromises[postCode];
};

const getPostcode = async (userPosition: CoordinatesInfo | null): Promise<string | null> => {
  if (!userPosition) return null;

  const { lat, lng } = userPosition;
  const cacheKey = `${lat}-${lng}`;

  if (postcodeByLatLongPromises[cacheKey]) {
    return postcodeByLatLongPromises[cacheKey];
  }

  postcodeByLatLongPromises[cacheKey] = getPostcodeByLatLong(lat, lng);
  return postcodeByLatLongPromises[cacheKey];
};

export const useStoreLocator = () => {
  const isMobile = useMedia('(max-width: 680px)', false);
  const [preferredPostcode, setPreferredPostcode] = useLocalStorage<string | null>('preferredPostcode', null);
  const [userPositionValue, setUserPositionValue] = useLocalStorage<CoordinatesInfo | null>('userLocation', null);
  const { googleMap } = useSelector((state: any) => state.app.context.project.configuration);

  const [loading, setLoading] = useState(false);
  const [chosenStore, setChosenStore] = useState<StoreItem | null>(null);

  const geolocationOptions = {
    enableHighAccuracy: true,
    timeout: 10000,
    maximumAge: 0,
  };

  const getUserPosition = useCallback(
    async (setError?: (error: string) => void, setPostCode?: (postcode: string) => void) => {
      if (!navigator.geolocation) return 'Geolocation is not supported by this browser.';
      setLoading(true);

      const errorCallback = (err: GeolocationPositionError) => {
        setError?.(err.message);
        setLoading(false);
      };

      navigator.geolocation.getCurrentPosition(
        async ({ coords }) => {
          const postcode = await getPostcode({ lat: coords.latitude, lng: coords.longitude });
          if (postcode) {
            setPostCode?.(postcode);
            setPreferredPostcode(postcode);
          }
          setUserPositionValue({ lat: coords.latitude, lng: coords.longitude });
          setLoading(false);
        },
        errorCallback,
        geolocationOptions,
      );
    },
    [setPreferredPostcode, setUserPositionValue],
  );

  const handlePostcodeChange = useCallback(
    (postcode: string) => {
      setPreferredPostcode(postcode);
    },
    [setPreferredPostcode],
  );

  const goBack = () => setChosenStore(null);

  const setStore = (storeData: StoreItem) => setChosenStore(storeData);

  return {
    setChosenStore,
    chosenStore,
    goBack,
    setStore,
    isMobile,
    userPosition: userPositionValue,
    preferredPostcode,
    loading,
    setPreferredPostcode,
    getUserPosition,
    setUserPositionValue,
    handlePostcodeChange,
    getPostCode: getPostcode,
    getRadius,
  };
};
